import { useState } from 'react';
import { Archive, ArchiveRestore, UploadCloud } from 'lucide-react';
import type { DocumentTemplateCategory, DocumentTemplateListItem } from '@ip/shared-types';
import { DOCUMENT_TEMPLATE_CATEGORIES } from '@ip/shared-types';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { t } from '@/i18n';

export type CategoryFilter = DocumentTemplateCategory | 'all';

const COLUMN_COUNT = 6;

interface TemplateListProps {
  templates: DocumentTemplateListItem[];
  loading: boolean;
  category: CategoryFilter;
  onCategoryChange: (category: CategoryFilter) => void;
  onRowClick: (template: DocumentTemplateListItem) => void;
  onToggleArchive: (template: DocumentTemplateListItem) => Promise<void>;
  /** Opens PublishDialog; omitted for templates with no published version or category 'contractor'. */
  onPublish: (template: DocumentTemplateListItem) => void;
}

function canPublish(template: DocumentTemplateListItem): boolean {
  return !template.archivedAt && template.currentVersion > 0 && template.category !== 'contractor';
}

/**
 * Templates list (Task 5, spec §8.2): category filter chips over the table.
 * The list always carries archived rows (DocumentsPage passes
 * includeArchived: true) — otherwise an archived template could never be
 * found again to restore, so the archive state shows as a row badge + the
 * row action flips between "Archive"/"Restore".
 */
export function TemplateList({
  templates,
  loading,
  category,
  onCategoryChange,
  onRowClick,
  onToggleArchive,
  onPublish,
}: TemplateListProps) {
  const [archiveTarget, setArchiveTarget] = useState<DocumentTemplateListItem | null>(null);
  const [archiving, setArchiving] = useState(false);

  const handleConfirmArchive = async () => {
    if (!archiveTarget) return;
    try {
      setArchiving(true);
      await onToggleArchive(archiveTarget);
      setArchiveTarget(null);
    } finally {
      setArchiving(false);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap gap-2">
        <Button
          size="sm"
          variant={category === 'all' ? 'default' : 'outline'}
          onClick={() => onCategoryChange('all')}
        >
          {t('common.all')}
        </Button>
        {DOCUMENT_TEMPLATE_CATEGORIES.map((c) => (
          <Button
            key={c}
            size="sm"
            variant={category === c ? 'default' : 'outline'}
            onClick={() => onCategoryChange(c)}
          >
            {t(`documents.templates.categories.${c}`)}
          </Button>
        ))}
      </div>

      <div className="bg-card rounded-xl border">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="text-muted-foreground">{t('documents.templates.name')}</TableHead>
                <TableHead className="text-muted-foreground w-32">{t('documents.templates.category')}</TableHead>
                <TableHead className="text-muted-foreground w-32">{t('documents.templates.visibility')}</TableHead>
                <TableHead className="text-muted-foreground w-32">{t('documents.templates.acceptance')}</TableHead>
                <TableHead className="text-muted-foreground w-24">{t('documents.templates.version')}</TableHead>
                <TableHead className="text-muted-foreground w-36">{t('documents.templates.updatedAt')}</TableHead>
                <TableHead className="text-muted-foreground w-24" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                Array.from({ length: 4 }, (_, i) => (
                  <TableRow key={i}>
                    <TableCell colSpan={COLUMN_COUNT + 1}>
                      <Skeleton className="h-8 w-full" />
                    </TableCell>
                  </TableRow>
                ))
              ) : templates.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={COLUMN_COUNT + 1} className="h-32 text-center">
                    <p className="text-muted-foreground">{t('documents.templates.empty')}</p>
                  </TableCell>
                </TableRow>
              ) : (
                templates.map((template) => (
                  <TableRow
                    key={template.id}
                    className="cursor-pointer"
                    onClick={() => onRowClick(template)}
                  >
                    <TableCell className="py-2">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{template.name}</span>
                        {template.archivedAt && (
                          <Badge variant="outline" className="text-muted-foreground">
                            {t('documents.templates.archived')}
                          </Badge>
                        )}
                      </div>
                      {template.description && (
                        <p className="text-muted-foreground text-xs">{template.description}</p>
                      )}
                    </TableCell>
                    <TableCell className="py-2">
                      <Badge variant="outline">
                        {t(`documents.templates.categories.${template.category}`)}
                      </Badge>
                    </TableCell>
                    <TableCell className="py-2">
                      <Badge variant="outline">
                        {t(`documents.templates.visibilities.${template.visibility}`)}
                      </Badge>
                    </TableCell>
                    <TableCell className="py-2">
                      <Badge variant="outline">
                        {t(`documents.templates.acceptances.${template.acceptanceMode}`)}
                      </Badge>
                    </TableCell>
                    <TableCell className="py-2">
                      {template.currentVersion > 0
                        ? `v${template.currentVersion}`
                        : t('documents.templates.draft')}
                    </TableCell>
                    <TableCell className="text-muted-foreground py-2 text-sm">
                      {new Date(template.updatedAt).toLocaleDateString()}
                    </TableCell>
                    <TableCell className="py-2">
                      <div className="flex items-center justify-end gap-1">
                        {canPublish(template) && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="size-8"
                            title={t('documents.publications.publish')}
                            aria-label={t('documents.publications.publish')}
                            onClick={(e) => {
                              e.stopPropagation();
                              onPublish(template);
                            }}
                          >
                            <UploadCloud className="size-4" />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="icon"
                          className="size-8"
                          onClick={(e) => {
                            e.stopPropagation();
                            setArchiveTarget(template);
                          }}
                        >
                          {template.archivedAt ? (
                            <ArchiveRestore className="size-4" />
                          ) : (
                            <Archive className="size-4" />
                          )}
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      <AlertDialog open={archiveTarget !== null} onOpenChange={(open) => !open && setArchiveTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {archiveTarget?.archivedAt
                ? t('documents.templates.unarchive')
                : t('documents.templates.archive')}
            </AlertDialogTitle>
            {!archiveTarget?.archivedAt && (
              <AlertDialogDescription>
                {t('documents.templates.archiveConfirm', { name: archiveTarget?.name ?? '' })}
              </AlertDialogDescription>
            )}
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={archiving}>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction disabled={archiving} onClick={handleConfirmArchive}>
              {archiveTarget?.archivedAt ? t('documents.templates.unarchive') : t('documents.templates.archive')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
